import { useInvoiceReview } from "@/modules/reviews/useInvoiceReview";
import { WalkInPaymentMethod } from "@/models/club";
import { hasUserPayment } from "./hasUserPayment";
/* eslint-disable @typescript-eslint/no-explicit-any */
import Header from "@/components/header";
import PersianSupportNumberInput from "@/components/persianNumberInput";
import { AppointmentQueueItemDto, CreateCustomerPurchaseCommandResponse, CreateCustomerPurchaseCommand, MerchantAppointmentQueueDto, MerchantServiceDto, MerchantUserDto } from "@/models/club";
import { Customer } from "@/models/response/customer";
import PriceSummary from "@/modules/peyment/PriceSummary";
import MobilePaymentSplit from "@/modules/peyment/MobilePaymentSplit";
import InvoiceDiscount, { AppliedDiscount } from "@/modules/peyment/InvoiceDiscount";
import SelectService from "@/modules/peyment/SelectService";
import CustomerInfo from "@/modules/schedule/appointment/CustomerInfo";
import SelectCustomer from "@/modules/schedule/customer/SelectCustomer";
import useInvoiceStaff, { commissionOf } from "@/modules/peyment/useInvoiceStaff";
import { ActionIcon, Blockquote, Button, Card, Checkbox, Container, Drawer, Group, Stack, Text, Title } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconPencil, IconPercentage, IconPlus, IconTrash } from "@tabler/icons-react";
import { UseMutateFunction } from "@tanstack/react-query";
import moment from "moment-jalaali";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useGetCustomerByPhoneNumber from "../../customer/hooks/useGetCustomerByPhoneNumber";
import UseGetCustomerById from "../../customer/hooks/useGetCustomerById";
import { schema, ServiceDto } from "../type";

// A chair renter keeps the whole price: the salon's commission does not apply to them (for now).
const isChairRenter = (member?: unknown) => Boolean((member as { isChairRenter?: boolean } | undefined)?.isChairRenter);



export type AddServiceDto = {
    service: MerchantServiceDto,
    price: number,
    percentage: number,
    profession: MerchantUserDto,
    id: string,
    isRemovable: boolean
}
type Props = {
    appointment?: MerchantAppointmentQueueDto,
    mutate: UseMutateFunction<CreateCustomerPurchaseCommandResponse, unknown, CreateCustomerPurchaseCommand, unknown>,
    isLoading: boolean
    // Previous invoice result unknown: submitting another one could duplicate it.
    locked?: boolean
    notices?: ReactNode
}

const ignorePaymentSplitChange = () => {};
const noDiscount: AppliedDiscount = { code: null, amount: 0, pending: false };

function AddPaymentMobile({ mutate, appointment, isLoading, locked, notices }: Props) {
  const [walkInPaymentMethod, setWalkInPaymentMethod] = useState<WalkInPaymentMethod>(WalkInPaymentMethod.Value1);
    const { teamMembers, allServices, allServicesFetching, commissionLocked } = useInvoiceStaff();
    const navigate = useNavigate();
    const [previewCustomer, { open: openCustomer, close: closeCustomer }] =
        useDisclosure(false);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
        null
    );
    const [preview, { open, close }] = useDisclosure(false);
    const [previewEdit, { open: previewOpen, close: previewClose }] = useDisclosure(false);
    const [services, setServices] = useState<ServiceDto[]>([]);
    const [discount, setDiscount] = useState<AppliedDiscount>(noDiscount);
    // Temporarily disabled: keep SnappPay state for future restoration.
    // const [paymentSplit, setPaymentSplit] = useState({ directAmount: 0, creditAmount: 0, valid: false });
    const { data: customerById } = UseGetCustomerById({ id: appointment?.customerId || 0 });
    const { data: customerByPhoneNumber } = useGetCustomerByPhoneNumber({ enabled: !!appointment?.customer?.phoneNumber, phoneNumber: appointment?.customer?.phoneNumber || "" });
    const appointmentCustomer = customerById || customerByPhoneNumber;

    const handleRemoveService = (id: string) => {
        setServices(services.filter(x => x.id !== id));
    }


    useEffect(() => {
        if (appointmentCustomer)
            setSelectedCustomer(appointmentCustomer)
    }, [appointmentCustomer]);

    useEffect(() => {
        if (!appointment?.appointments || !allServices || !teamMembers) return;
        setServices((prev) => {
            const newItems = appointment?.appointments as AppointmentQueueItemDto[];
            const merged = [...prev];
            newItems?.filter(x => !x.purchaseRegistered).forEach((item) => {
                const exist = merged.find((x) => Number(x.id) === item.appointmentId);
                if (!exist) {
                    const findService = allServices?.flatMap(x => x.services).find(x => x?.id === item.merchantServiceId)
                    const find = teamMembers?.find(x => x.id === item?.merchantUserId);
                    merged.push({
                        id: item?.appointmentId?.toString() || "",
                        profession: find || {},
                        service: findService || {},
                        price: findService?.startingPrice || 0,
                        percentage: commissionOf(find),
                        isRemovable: false,
                        isReserved: true,
                        isSelected: false,
                        appointmentId: appointment.id,
                        hasPayment: hasUserPayment(appointment),
                        paymentPrice: appointment.rateBreakdown?.price,
                        appointmentDate: item?.appointmentDate,
                        endTime: moment(item?.endTime, "HH:mm:ss").format("HH:mm"),
                        startTime: moment(item?.startTime, "HH:mm:ss").format("HH:mm"),
                    });
                }
            });
            return merged;
        });
    }, [appointment, allServices, teamMembers]);

    const summary = useMemo(() => {
        const paidAppointmentQueueIds = new Set<number>();
        return services.filter(x => !x.isReserved || (x.isReserved && x.isSelected)).reduce(
            (acc, item) => {
                const price = item.price;
                const percentage = item.percentage;
                const commission = (price * (100 - percentage)) / 100;
                const providerShare = (price * percentage) / 100;
                const alreadyCounted = item.appointmentId != null && paidAppointmentQueueIds.has(item.appointmentId);
                const userPayment = item.hasPayment && !alreadyCounted ? item.paymentPrice || 0 : 0;
                if (item.hasPayment && item.appointmentId != null) {
                    paidAppointmentQueueIds.add(item.appointmentId);
                }
                acc.totalPrice += price - userPayment;
                acc.totalService += price;
                acc.totalUserPayments += userPayment;
                acc.totalCommission += commission;
                acc.totalProviderShare += providerShare;
                return acc;
            },
            {
                totalPrice: 0,
                totalService: 0,
                totalUserPayments: 0,
                totalCommission: 0,
                totalProviderShare: 0,
            }
        );
    }, [services]);

    // Owed after the deposit; the discount comes off this.
    const owed = Math.max(0, summary.totalPrice);
    const payable = Math.max(0, owed - discount.amount);

    const handleReservedSelection = (service: ServiceDto, isSelected: boolean) => {
        setServices((prev) =>
            prev.map((item) =>
                item.isReserved &&
                service.appointmentId != null &&
                item.appointmentId === service.appointmentId
                    ? { ...item, isSelected }
                    : item
            )
        );
    };

    const review = useInvoiceReview(
      selectedCustomer,
      services.filter(x => !x.isReserved || x.isSelected).map(x => ({ merchantServiceId: x.service?.id, merchantUserId: x.profession?.id })),
      isLoading,
    );

    const createFactor = () => {
      if (isLoading || locked || review.blocked || discount.pending) return;
        if (!selectedCustomer) {
            notifications.show({
                message: "لطفا کاربر را انتخاب نمایید",
                color: 'yellow'
            });
            return;
        }

        mutate({
            customerId: selectedCustomer ? (selectedCustomer as Customer)?.id : undefined,
            description: "",
            reviewInvitation: review.payload,
            // directAmount: paymentSplit.directAmount,
            discountCode: discount.code && discount.amount > 0 ? discount.code : undefined,
            directAmount: payable,
            // creditAmount: paymentSplit.creditAmount,
            creditAmount: 0,
            items: services.filter(x => !x.isReserved || (x.isReserved && x.isSelected)).map(x => ({
                amount: x.price,
                appointmentId: (appointment && x.isReserved) ? Number(x.id) : undefined,
                appointmentQueueId: (appointment && x.isReserved) ? appointment?.id : undefined,
                isWalkIn: !x?.isReserved,
      walkInPaymentMethod: !x?.isReserved ? walkInPaymentMethod : undefined,
                markupPercentage: x.percentage,
                merchantServiceId: x?.service?.id,
                merchantUserId: x?.profession?.id
            }))
        }, {
            onSuccess() {
                review.reset();
                notifications.show({
                    // message: result.paymentLinkUrl
                    //   ? "فاکتور ثبت و لینک پرداخت برای مشتری پیامک شد"
                    //   : "فاکتور با موفقیت ثبت گردید",
                    message: "فاکتور با موفقیت ثبت گردید",
                    color: "green"
                })
                setSelectedCustomer(null);
                setServices([]);
                setDiscount(noDiscount);
            }
        })
    }

    const form = useForm<{
        price: number | undefined, percentage: number | undefined, id?: number | string
    }>({
        initialValues: {
            price: undefined,
            percentage: undefined,
        },
        validate: yupResolver(schema)
    });



    const submit = (values: any) => {
        if (values?.id) {
            setServices((prev) =>
                prev.map((item) => {
                    if (item.id === values.id) {
                        return {
                            ...item,
                            percentage: values?.percentage || 0,
                            price: values?.price || 0,
                            service: item.service,
                            profession: item.profession,
                        };
                    }

                    return item;
                })
            );
            previewClose();
            form.reset();
        }
    };

    const handleEdit = (id: string) => {
        const findService = services.find(x => x.id === id);
        form.setValues({
            percentage: findService?.percentage ?? 0,
            price: findService?.price,
            id
        })
        previewOpen();
    }

    return (
        <>
            <Header
                title="افزودن فاکتور"
                showBack
            />
            <Container w="100%" size="md" pb="md">
                <Stack>
                    {notices}
                    <Title order={4}>افزودن فاکتور</Title>
                    <CustomerInfo selectedCustomer={selectedCustomer} openCustomer={openCustomer} />
                    <Title order={5}> خدمات </Title>
                    {
                        services?.length === 0 ?
                            <Card withBorder mih={100}>
                                <Stack justify="center" align="center">
                                    <Text size="xs" c="dimmed">
                                        خدمات انجام شده را وارد نمایید
                                    </Text>
                                    <Button
                                        variant="outline"
                                        radius="lg"

                                        leftSection={<IconPlus size={15} />}
                                        size="xs"
                                        onClick={open}
                                    >
                                        <Text size="xs">افزودن خدمت</Text>
                                    </Button>
                                </Stack>
                            </Card> :
                            <Stack gap="md">
                                <Stack gap="5" align="flex-start" mt="md" px="xs">
                                    {services.map((service) => (
                                        <Blockquote
                                            key={service?.id}
                                            color={service?.isReserved ? "blue" : "yellow"}
                                            bg="white"
                                            p="xs"
                                            w="100%"
                                        >
                                            <Group justify="space-between" wrap="nowrap">
                                                <Group wrap="nowrap" align="flex-start" w="100%">
                                                    {service?.isReserved && (
                                                        <Checkbox
                                                            mt="xs"
                                                            checked={service?.isSelected}
                                                            onChange={(event) => {
                                                                handleReservedSelection(
                                                                    service,
                                                                    event.currentTarget.checked
                                                                );
                                                            }}
                                                        />
                                                    )}

                                                    <Stack gap="2" w="100%">
                                                        <Group justify="space-between" w="100%">
                                                            <Text size="sm" fw="500">
                                                                {service?.service?.title}
                                                            </Text>
                                                            <Group gap="0" p="0" wrap="nowrap">
                                                                <ActionIcon
                                                                    variant="transparent"
                                                                    color="yellow"
                                                                    onClick={() => handleEdit(service?.id)}
                                                                >
                                                                    <IconPencil stroke={1} size={20} />
                                                                </ActionIcon>

                                                                {!service?.isReserved && (
                                                                    <ActionIcon
                                                                        variant="transparent"
                                                                        color="red"
                                                                        onClick={() =>
                                                                            handleRemoveService(service?.id)
                                                                        }
                                                                    >
                                                                        <IconTrash stroke={1} size={20} />
                                                                    </ActionIcon>
                                                                )}
                                                            </Group>
                                                        </Group>


                                                        <Text size="sm" fw="500">
                                                            {service?.profession?.firstName}{" "}
                                                            {service?.profession?.lastName}
                                                        </Text>

                                                        <Group gap="xs" wrap="nowrap">
                                                            <Text size="xs" c="dimmed">
                                                                {service?.price.toLocaleString()} ریال
                                                            </Text>

                                                            <Text size="xs" c="dimmed">
                                                                -
                                                            </Text>

                                                            <Text size="xs" c="dimmed">
                                                                {isChairRenter(service?.profession) ? "اجاره‌ای · بدون کمیسیون" : `${service?.percentage} % (کمیسیون)`}
                                                            </Text>
                                                        </Group>
                                                        {
                                                            service?.isReserved && <Group gap="xs" wrap="nowrap">

                                                                <Text size="xs" c="dimmed">
                                                                    {moment(service?.appointmentDate).format("jYYYY/jMM/jDD")}
                                                                </Text>

                                                                <Text size="xs" c="dimmed">
                                                                    |
                                                                </Text>

                                                                <Text size="xs" c="dimmed">
                                                                    {service?.startTime}
                                                                </Text>
                                                                <Text size="xs" c="dimmed">
                                                                    -
                                                                </Text>
                                                                <Text size="xs" c="dimmed">
                                                                    {service?.startTime} - {service?.endTime}
                                                                </Text>
                                                            </Group>
                                                        }
                                                    </Stack>
                                                </Group>


                                            </Group>
                                        </Blockquote>
                                    ))}
                                </Stack>
                                <Group justify="center">
                                    <Button
                                        variant="outline"
                                        radius="lg"
                                        leftSection={<IconPlus size={15} />}
                                        size="xs"
                                        onClick={open}
                                    >
                                        <Text size="xs">افزودن خدمت</Text>
                                    </Button>
                                </Group>
                            </Stack>
                    }

                    {/* <Title order={5}> پرداخت ها </Title> */}
                    <Title order={5}>  فاکتور </Title>
                    {
                        services.length > 0 && <PriceSummary summary={{ ...summary, totalPrice: owed }} discount={discount} />
                    }
                    {summary.totalUserPayments > 0 && (
                        <Group justify="space-between">
                            <Text size="sm">پرداخت کاربر (ریال)</Text>
                            <Text size="sm">{summary.totalUserPayments.toLocaleString()}</Text>
                        </Group>
                    )}
                    {services.length > 0 && <InvoiceDiscount customerId={selectedCustomer?.id} owed={owed} onChange={setDiscount} />}
                    <MobilePaymentSplit directOnly onMethodChange={setWalkInPaymentMethod} totalAmount={payable} customerSelected={!!selectedCustomer} onChange={ignorePaymentSplitChange} />

                </Stack>
                <SelectCustomer
                    previewCustomer={previewCustomer}
                    closeCustomer={closeCustomer}
                    setSelectedCustomer={setSelectedCustomer}
                />
                <SelectService commissionLocked={commissionLocked} allServices={allServices} teamMembers={teamMembers} loading={allServicesFetching} setServices={setServices} opened={preview} onClose={close} />
            </Container >
            <Card
                withBorder
                radius={0}
                mt="auto"
                style={{ flexShrink: 0, paddingBottom: "calc(var(--mantine-spacing-md) + env(safe-area-inset-bottom, 0px))" }}
            >
                <Stack gap="sm">
                {review.mobileControl}
                <Group gap="sm" wrap="nowrap">
                    <Button
                        size="md"
                        fullWidth
                        type="submit"
                        style={{ flex: 2, minWidth: 0 }}
                        form="add-service"
                        onClick={createFactor}
                        loading={isLoading}
                        // disabled={!selectedCustomer || services.length === 0 || !paymentSplit.valid}
                        disabled={locked || review.blocked || discount.pending || !selectedCustomer || services.length === 0 || summary.totalService <= 0}

                    >
                        <Group wrap="nowrap">
                            <Text size="sm">
                                {/* {paymentSplit.creditAmount > 0 ? "ثبت و ارسال لینک" : "ثبت"} */}
                                ثبت فاکتور
                            </Text>
                            {
                                services.length > 0 && <Text size="xs">
                                    {payable.toLocaleString()} ریال

                                </Text>
                            }

                        </Group>

                    </Button>
                    <Button
                        size="md"
                        style={{ flex: 1, minWidth: 0 }}
                        fullWidth
                        variant="outline"
                        color="red"
                        onClick={() => navigate(-1)}
                    >
                        انصراف
                    </Button>
                </Group>
                </Stack>
            </Card>

            <Drawer opened={previewEdit} onClose={previewClose} radius="md"
                position="bottom"
                size="md">
                <Stack>
                    <Stack gap="5">
                        <Text fw="bold" size="lg">
                            مبلغ و کمیسیون
                        </Text>
                        <Text size="xs" c="dimmed">
                            مبلغ سرویس و کمیسیون را وارد نمایید
                        </Text>
                    </Stack>

                    <form
                        onSubmit={form.onSubmit(submit)}
                        className="w-100"
                    >
                        <Stack pb="md">
                            <PersianSupportNumberInput
                                size="lg"
                                withThousandsSeparator
                                withAsterisk
                                value={form?.values?.price?.toString() || ""}
                                leftSection={<Text>ریال</Text>}
                                styles={{
                                    input: {
                                        direction: "ltr",
                                        textAlign: "left",
                                        paddingLeft: "1rem",
                                    },
                                    label: {
                                        fontSize: "1rem",
                                    },
                                    description: {
                                        fontSize: "0.8rem"
                                    }
                                }}
                                label="قیمت"
                                description="قیمت سرویس را وارد نمایید"
                                onChangeHandler={(e) =>
                                    form.setValues({
                                        price: e ? Number(e) : undefined,
                                    })
                                }
                                error={form?.errors?.price}

                            />
                            <PersianSupportNumberInput
                                rightSection={<IconPercentage size="15" />}
                                size="lg"
                                withAsterisk
                                value={form?.values?.percentage?.toString() || ""}
                                withThousandsSeparator

                                styles={{
                                    input: {
                                        direction: "ltr",
                                        textAlign: "left",
                                        paddingLeft: "2.5rem",

                                    },
                                    label: {
                                        fontSize: "1rem",
                                    },
                                    description: {
                                        fontSize: "0.8rem"
                                    }
                                }}
                                label={"درصد کمیسون"}
                                disabled={commissionLocked || isChairRenter(services.find((s) => s.id === form.values.id)?.profession)}
                                description={isChairRenter(services.find((s) => s.id === form.values.id)?.profession) ? "آرایشگر اجاره‌ای؛ کمیسیون ندارد" : undefined}
                                onChangeHandler={(e) =>
                                    form.setValues({
                                        percentage: e ? Number(e) : undefined,
                                    })
                                }
                                error={form?.errors?.percentage}
                            />

                            <Button fullWidth type="submit" size="lg">
                                ثبت
                            </Button>
                        </Stack>
                    </form>
                </Stack>
            </Drawer>
        </>
    )
}

export default AddPaymentMobile
